import React, { useState, useCallback } from 'react';
import Dialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import { ITypeTreeConditionObject } from '../../../../../../Types/UrvalTypes';
import EDTreeSelect from '../../../../../../BaseComponents/TreeSelect/EDTreeSelect';
import {
  ITreeSection,
  ICheckedItems,
} from '../../../../../../BaseComponents/TreeSelect/TreeSelectUtils';
import { getTreePickerSections } from './Utils/typeConditionUtils';

interface IProps {
  open: boolean;
  onClose: () => void;
  conditionObject: ITypeTreeConditionObject;
  setConditionObject: (newConditionObject: ITypeTreeConditionObject) => void;
}

// Recursively set checked on a node and all its descendants.
const cascadeChecked = (node: ITreeSection, checked: boolean): ITreeSection => {
  if (!node.subList || node.subList.length === 0) {
    return { ...node, checked };
  }
  return {
    ...node,
    checked,
    subList: node.subList.map(child => cascadeChecked(child, checked)),
  };
};

const allLeavesChecked = (nodes: ITreeSection[]): boolean =>
  nodes.every(node =>
    node.subList && node.subList.length > 0
      ? allLeavesChecked(node.subList)
      : node.checked === true
  );

const anyLeafChecked = (nodes: ITreeSection[]): boolean =>
  nodes.some(node =>
    node.subList && node.subList.length > 0
      ? anyLeafChecked(node.subList)
      : node.checked === true
  );

// Walk the full tree, cascade only from the node matching targetId downward.
// On the way back up, recalculate parent checked state from its children.
const applyCheck = (
  nodes: ITreeSection[],
  targetId: string,
  checked: boolean
): { nodes: ITreeSection[]; found: boolean } => {
  let found = false;
  const updated = nodes.map(node => {
    if (node.id === targetId) {
      found = true;
      return cascadeChecked(node, checked);
    }
    if (node.subList) {
      const result = applyCheck(node.subList, targetId, checked);
      if (result.found) {
        found = true;
        const updatedNode = { ...node, subList: result.nodes };
        // Recalculate this parent's checked state based on its children
        updatedNode.checked = allLeavesChecked(result.nodes);
        updatedNode.indeterminate =
          !updatedNode.checked && anyLeafChecked(result.nodes);
        return updatedNode;
      }
    }
    return node;
  });
  return { nodes: updated, found };
};

const TypeTreePickerDialog: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { setConditionObject, onClose } = props;
  const getInitialState = useCallback(() => {
    return getTreePickerSections(props.conditionObject);
  }, [props.conditionObject]);
  const [sections, setSections] = useState(getInitialState());

  const handleCheck = useCallback(
    (
      currentSections: ITreeSection[],
      checkedSection: ICheckedItems,
      checked: boolean,
      suggestion?: ITreeSection
    ): ITreeSection[] => {
      // suggestion is the actual clicked node passed from EDTreeSelectListItem
      const clickedId = suggestion?.id ?? checkedSection.checkedItem.id;
      const { nodes } = applyCheck(currentSections, clickedId, checked);
      return nodes;
    },
    []
  );

  const commit = useCallback(() => {
    const section = sections[0];
    if (section.data) {
      const collectChecked = (items: typeof section.subList): string[] =>
        (items ?? []).reduce<string[]>((acc, x) => {
          if (x.checked && (!x.subList || x.subList.length === 0)) {
            acc.push(x.value.toUpperCase());
          }
          return acc.concat(collectChecked(x.subList));
        }, []);

      setConditionObject({
        ...section.data,
        condition: collectChecked(section.subList),
      });
      onClose();
    }
  }, [sections, setConditionObject, onClose]);

  return (
    <Dialog
      open={props.open}
      title="Välj Typ"
      onClose={props.onClose}
      footerActions={[
        { onClick: props.onClose, label: 'Avbryt' },
        { onClick: commit, label: 'OK' },
      ]}
      onExited={getInitialState}
    >
      <EDTreeSelect
        sections={sections}
        sectionsUpdate={setSections}
        searchValue=""
        onCheck={handleCheck}
      />
    </Dialog>
  );
};

export default TypeTreePickerDialog;
